import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Mail, Lock, ChevronLeft, CheckCircle } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="text-zinc-500 hover:text-zinc-300 flex items-center gap-1 text-sm mb-8">
          <ChevronLeft className="w-4 h-4" /> Volver al login
        </Link>

        <div className="glass-card p-8">
          {sent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-medium text-zinc-100">Correo enviado</h1>
              <p className="text-sm text-zinc-400">
                Si el correo existe en MorphMX, recibirás un enlace para restablecer tu contraseña.
              </p>
              <p className="text-xs text-zinc-600">Revisa tu bandeja de spam si no lo ves en tu inbox.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-400" />
                </div>
                <h1 className="text-2xl font-medium text-zinc-100">Recuperar contraseña</h1>
              </div>
              <p className="text-sm text-zinc-400 mb-6">
                Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-300 block mb-2">Correo electrónico</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-purple-500/50"
                    placeholder="tu@correo.com"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-zinc-100 text-zinc-900 hover:bg-white"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Enviando...' : 'Enviar enlace de recuperación'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Las contraseñas no coinciden'); return; }
    if (password.length < 8) { toast.error('Mínimo 8 caracteres'); return; }
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center p-6">
        <div className="glass-card p-8 text-center">
          <p className="text-zinc-400">Token inválido. Solicita un nuevo enlace de recuperación.</p>
          <Link to="/forgot-password" className="text-purple-400 text-sm mt-4 block">Solicitar nuevo enlace</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="glass-card p-8">
          {done ? (
            <div className="text-center space-y-4">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto" />
              <h1 className="text-2xl font-medium text-zinc-100">Contraseña restablecida</h1>
              <p className="text-sm text-zinc-400">Redirigiendo al login...</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-purple-400" />
                </div>
                <h1 className="text-2xl font-medium text-zinc-100">Nueva contraseña</h1>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-300 block mb-2">Nueva contraseña</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-purple-500/50"
                    placeholder="Mínimo 8 caracteres"
                    required
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-300 block mb-2">Confirmar contraseña</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-purple-500/50"
                    placeholder="Repite la contraseña"
                    required
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !password || !confirm}
                  className="w-full bg-zinc-100 text-zinc-900 hover:bg-white"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Restableciendo...' : 'Restablecer contraseña'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
